/**
 * PublicArtifactDetail
 *
 * Read-only detail panel for a selected public artifact (task / note /
 * obstruction). The URL selects the artifact (descriptor), and the full DTO is
 * fetched lazily — while it loads the panel body shows a skeleton, and a fetch
 * failure renders an error state. Mounts inside a `SlidePanel` with
 * `withExpandButton` and NO `onEdit` / `onDelete` — the public viewer cannot
 * mutate. Bodies reuse the authenticated detail panels in read-only mode;
 * tab/field visibility honors the share's `Show*` flags (the backend already
 * strips hidden fields to null). Task↔note relations open through the shared
 * `NavigationTabs` strip — exactly like the logged-in app — with out-of-share
 * relations rendered non-clickable.
 *
 * @module components/share/public/PublicArtifactDetail
 */
import { memo, useCallback, useEffect, useMemo, type ReactNode } from 'react';
import { Stack, Title } from '@mantine/core';
import { useTranslation } from 'react-i18next';

import { SlidePanel } from '@/components/ui/SlidePanel';
import { LoadingState } from '@/components/common';
import { EmptyState } from '@/components/common/EmptyState';
import { NavigationTabs } from '@/components/common/NavigationTabs';
import { TaskDetailsHeader } from '@/components/tasks/TaskDetailsPanel';
import { DetailHeader } from '@/components/notes/NoteDetailPanel';
import { ObstructionDetailsHeader } from '@/components/obstructions/ObstructionDetailsPanel';
import { ObstructionGeneralInfo } from '@/components/obstructions/panel/ObstructionGeneralInfo';
import { useGeoMarkupStore } from '@/store';
import { useDeviceLayout, useMobilePreviewMode } from '@/hooks';
import { RIGHT_PANEL_MIN_WIDTH } from '@/constants/ui';
import type { PublicShareConfig, PublicShareListData } from '@/types/share';

import {
  mapPublicTask,
  mapPublicNoteDetail,
  mapPublicObstructionDetail,
} from '@/store/mappers/publicShareMapper';
import { SHARE_MANAGER_PANEL_WIDTH } from '../../shareManagerConstants';
import type { PublicArtifactDescriptor } from '../usePublicArtifactSelection';
import type { PublicArtifactDetailSlot } from '../usePublicArtifactDetail';
import { usePublicDetailNavigation } from '../usePublicDetailNavigation';
import { usePublicDetailZoom } from '../usePublicDetailZoom';
import { PublicTaskDetail } from './parts/PublicTaskDetail';
import { PublicNoteDetail } from './parts/PublicNoteDetail';
import styles from './PublicArtifactDetail.module.css';

interface PublicArtifactDetailProps {
  /** URL-resolved reference to the open artifact; null when closed. */
  descriptor: PublicArtifactDescriptor | null;
  /** Lazily-loaded kind + full DTO for the open artifact; null while loading/closed. */
  artifact: PublicArtifactDetailSlot | null;
  isDetailLoading: boolean;
  detailError: boolean;
  /** Share config — drives tab/field visibility (Show* flags). */
  config: PublicShareConfig;
  /** Slim share list — decides which relations are navigable. */
  listData: PublicShareListData;
  /** Share token — used to build public attachment download/preview URLs. */
  token: string;
  /**
   * Extra controls merged into the panel toolbar (left of the window controls)
   * — used on the shared map to fold the language/theme switchers into the
   * panel's bar instead of a separate floating pill. Desktop only.
   */
  toolbarActions?: ReactNode;
  onClose: () => void;
}

export const PublicArtifactDetail = memo(function PublicArtifactDetail({
  descriptor,
  artifact,
  isDetailLoading,
  detailError,
  config,
  listData,
  token,
  toolbarActions,
  onClose,
}: PublicArtifactDetailProps) {
  const { t } = useTranslation();

  const {
    tabs,
    activeTabId,
    handleTabClick,
    handleTabClose,
    handlePanelClose,
    handleViewTask,
    handleViewNote,
  } = usePublicDetailNavigation(descriptor, onClose);

  // Fly the map to the active artifact whenever it changes — opening a related
  // task/note tab, switching tabs, or closing a tab back to the previous one.
  usePublicDetailZoom(descriptor);

  const publicTask = artifact?.kind === 'task' ? artifact.detail : null;
  const publicNote = artifact?.kind === 'note' ? artifact.detail : null;
  const publicObstruction =
    artifact?.kind === 'obstruction' ? artifact.detail : null;

  const task = useMemo(
    () => (publicTask ? mapPublicTask(publicTask) : null),
    [publicTask]
  );

  const note = useMemo(
    () => (publicNote ? mapPublicNoteDetail(publicNote) : null),
    [publicNote]
  );

  const obstruction = useMemo(
    () =>
      publicObstruction ? mapPublicObstructionDetail(publicObstruction) : null,
    [publicObstruction]
  );

  // Enforce the "one side panel" rule: opening (or switching) a detail closes
  // the layers panel — mirroring `applyOpenDetailsPanel` in the authenticated
  // map. The public flow opens the detail straight from the URL and never goes
  // through `slice.openDetails()`, so we replicate just that side effect here.
  // Keyed on the artifact id (not a boolean) so switching task A → B re-closes
  // the layers panel, while re-opening it over an unchanged detail is allowed.
  const closeLayersPanel = useGeoMarkupStore(
    (state) => state.ui.closeLayersPanel
  );
  const selectionId = descriptor?.id ?? null;
  useEffect(() => {
    if (selectionId !== null) {
      closeLayersPanel();
    }
  }, [selectionId, closeLayersPanel]);

  // Mirror the authenticated detail panels' fullscreen layout: sidebar mode
  // on desktop fullscreen, plus a right panel (mini-map + comments) on wide
  // screens for tasks.
  const isFullscreen = useGeoMarkupStore(
    (state) => state.ui.slidePanel.isFullscreen
  );
  const { isMobile, width } = useDeviceLayout();
  const isDesktopFullscreen = isFullscreen && !isMobile;
  const showSidebar = isDesktopFullscreen && (task !== null || note !== null);
  const showRightPanel =
    showSidebar && task !== null && width >= RIGHT_PANEL_MIN_WIDTH;

  // Mirror the main map: on mobile the detail panel opens in partial preview
  // (map visible on top) instead of full height; swipe-up expands it.
  const { previewMode, setPreviewMode } = useMobilePreviewMode(
    isMobile,
    selectionId
  );

  // Note → linked task navigation: clickable only when the task is in the share.
  const noteLinkedTask = note?.linkedTasks[0];
  const noteLinkedTaskInShare =
    noteLinkedTask !== undefined &&
    listData.tasks.some(
      (shareTask) =>
        shareTask.id === noteLinkedTask.id ||
        shareTask.shortId === noteLinkedTask.shortId
    );

  const handleNoteTaskNavigate = useCallback(
    (taskId: string, title: string, shortId?: string) => {
      const linkedShortId =
        shortId ??
        note?.linkedTasks.find((linkedTask) => linkedTask.id === taskId)
          ?.shortId ??
        taskId;
      handleViewTask({ id: taskId, shortId: linkedShortId, title });
    },
    [note?.linkedTasks, handleViewTask]
  );

  const headerNode =
    task !== null ? (
      <TaskDetailsHeader task={task} />
    ) : note !== null ? (
      <DetailHeader note={note} />
    ) : obstruction !== null ? (
      <ObstructionDetailsHeader obstruction={obstruction} />
    ) : (
      <Title order={4}>{descriptor?.title ?? ''}</Title>
    );

  // NavigationTabs self-hides for ≤1 tab; stacked above the header exactly
  // like the authenticated panels.
  const tabsNode = (
    <NavigationTabs
      tabs={tabs}
      activeTabId={activeTabId}
      onTabClick={handleTabClick}
      onTabClose={handleTabClose}
    />
  );

  // In sidebar mode the header moves into the center column (sidebarHeader),
  // so the top bar shows only the navigation tab strip.
  const headerContent = showSidebar ? (
    tabs.length > 1 ? (
      tabsNode
    ) : undefined
  ) : (
    <Stack gap="xs" className={styles.headerStack}>
      {tabsNode}
      {headerNode}
    </Stack>
  );

  // Shown both for an explicit fetch failure and as the (unreachable, but kept
  // defensive) fallback after all artifact kinds — defined once so the error
  // presentation has a single source.
  const detailErrorState = <EmptyState title={t('share.public.detailError')} />;

  // `mergedToolbar` renders the panel as one continuous themed bar in desktop
  // fullscreen (transparent toolbar + flush content + suppressed default
  // TopBar) and self-gates to that mode, so docked/sidepanel keeps the original
  // chrome (floating pill over the map + plain gray toolbar). The language/theme
  // controls are folded in only when fullscreen (otherwise the floating pill
  // shows them, so passing them here would duplicate the pair).
  return (
    <SlidePanel
      opened={descriptor !== null}
      onClose={handlePanelClose}
      headerContent={headerContent}
      mergedToolbar
      toolbarActions={isDesktopFullscreen ? toolbarActions : undefined}
      width={SHARE_MANAGER_PANEL_WIDTH}
      withCloseButton
      withExpandButton
      fullscreenWideContent={showSidebar}
      defaultFullscreen={false}
      previewMode={isMobile && previewMode}
      onPreviewModeChange={setPreviewMode}
      mobileBelowSidePanel
      closeButtonAriaLabel={t('common.close')}
    >
      {detailError ? (
        detailErrorState
      ) : isDetailLoading || artifact === null ? (
        <LoadingState text={t('share.public.detailLoading')} centered />
      ) : task !== null && publicTask !== null ? (
        <PublicTaskDetail
          task={task}
          publicTask={publicTask}
          listData={listData}
          config={config}
          token={token}
          showSidebar={showSidebar}
          showRightPanel={showRightPanel}
          onViewTask={handleViewTask}
          onViewNote={handleViewNote}
        />
      ) : note !== null ? (
        <PublicNoteDetail
          note={note}
          token={token}
          showSidebar={showSidebar}
          onTaskNavigate={
            noteLinkedTaskInShare ? handleNoteTaskNavigate : undefined
          }
        />
      ) : obstruction !== null ? (
        <ObstructionGeneralInfo obstruction={obstruction} readOnly />
      ) : (
        detailErrorState
      )}
    </SlidePanel>
  );
});
